import React, { useEffect, useState, useRef } from "react";
import apiDefinitions from "../../../api/apiDefinitions";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { Link } from "react-router-dom";
import { Pagination, Form, OverlayTrigger, Popover } from "react-bootstrap";
import {
  priority,
  status,
} from "../../../core/common/selectoption/selectoption";
import toast from "react-hot-toast";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FileUpload from "./fileUploader";
import Swal from "sweetalert2";
import Select, { MultiValue } from "react-select";
import * as XLSX from "xlsx";

const ViewAllLeads = () => {
  const route = all_routes;
  const navigate = useNavigate();
  const hasDataLoaded = useRef(false);
  const [employeeId, setEmployeeId] = useState("");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchKey, setSearchKey] = useState("");
  const [searchClicked, setSearchClicked] = useState(false);
  const [leadStatus, setLeadStatus] = useState("");
  const [leadStatusExport, setLeadStatusExport] = useState("");
  const [leadPriority, setLeadPriority] = useState("");
  const [followUpDate, setFollowUpDate] = useState<Date | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File[]>([]);
  const [clearFileUploader, setClearFileUploader] = useState(false);
  const [employeeList, setEmployeeList] = useState<any[]>([]);
  const [isAssignedByValid, setIsAssignedByValid] = useState(false);
  const [filterAssignedBy, setFilterAssignedBy] = useState("");
  const [filterCourseId, setFilterCourseId] = useState("");
  const [representativeList, setRepresentativeList] = useState<any[]>([]);
  const [inquiryList, setInquiryList] = useState<any[]>([]);

  interface DecodedToken {
    exp: number;
    [key: string]: any;
  }

  interface Lead {
    id: number;
    fullname: string;
    contactNumber: number;
    email: number;
    nic: string;
    followUpDate: string;
    leadPriorityLevelId: number;
    leadStatus: {
      status_name: string;
      color: string;
    };
    searchKey: string;
    searchClicked: boolean;
    leadPriority: string;
    followUp: string;
    assignStatus: number;
    assignedBy: string;
    courseId: {
      course_id: number;
      course_name: string;
      course_category_id: number;
    }[];
  }

  interface FetchLeadsResponse {
    content: Lead[];
    totalElements: number;
    totalPages: number;
  }

  interface FetchLeadsStatusResponse {
    content: Lead[];
    totalElements: number;
    totalPages: number;
  }

  interface FetchLeadsPriorityResponse {
    content: Lead[];
    totalElements: number;
    totalPages: number;
  }

  const handleSearch = () => {
    setSearchClicked(!searchClicked);
  };

  const handleClose = () => {
    setSearchClicked(!searchClicked);
    setSearchKey("");
    setLeadPriority("");
    setLeadStatus("");
    setFollowUpDate(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("token");
      if (!token) {
        console.error("No token found. Please log in again.");
        navigate(route.login); // Redirect to login if no token
        return;
      }

      try {
        const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
        const currentTime = Math.floor(Date.now() / 1000);

        if (decodedToken.exp && decodedToken.exp <= currentTime) {
          console.log("Session expired. Please log in again.");
          navigate(route.login); // Redirect to login if token expired
          return;
        }

        if (!hasDataLoaded.current) {
          hasDataLoaded.current = true;
          return;
        }

        const offset = currentPage - 1;
        console.log("Fetching leads with offset", offset);

        const toastId = toast.loading("Loading Data...");
        try {
          const response = await apiDefinitions.getAllLeadsAndSearch(
            token,
            offset,
            itemsPerPage,
            searchKey,
            leadPriority,
            leadStatus,
            formatDate(followUpDate) || "",
            filterCourseId,
            filterAssignedBy
          );

          if (response.data.code === 200) {
            toast.dismiss(toastId);
            toast.success("Data Loaded", { duration: 5000 });
            const data: FetchLeadsResponse = response.data.data;
            console.log("Fetched leads", data.content);
            setLeads(data.content);
            setTotalPages(data.totalPages);
          } else if (response.data.code === 404) {
            toast.dismiss(toastId);
            toast.error("No Leads Were Found", { duration: 5000 });
          } else {
            toast.dismiss(toastId);
            toast.error("Failed to fetch leads. Please try again.");
          }
        } catch (error) {
          toast.dismiss(toastId);
          console.error("Failed to fetch leads", error);
          toast.error("An error occurred. Please try again.");
        }
      } catch (error) {
        console.error("Invalid token or unable to decode", error);
        navigate(route.login); // Redirect if token is invalid
      }
    };

    fetchData();
  }, [
    currentPage,
    itemsPerPage,
    searchClicked,
    leadPriority,
    leadStatus,
    navigate,
    filterCourseId,
    filterAssignedBy,
    route.login,
  ]);

  const handlePageChange = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  useEffect(() => {
    console.log("Current page:", currentPage);
  }, [currentPage]);

  interface ReasonPopoverProps {
    reason: string;
  }

  const renderReasonPopover = ({ reason }: ReasonPopoverProps): JSX.Element => (
    <Popover id="popover-reason">
      <Popover.Body>{reason}</Popover.Body>
    </Popover>
  );

  useEffect(() => {
    console.log("Selected Date:", followUpDate);
  }, [followUpDate]);

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const newDate = new Date(
      date.getTime() + 5 * 60 * 60 * 1000 + 30 * 60 * 1000
    ); // Add 5 hours and 30 minutes
    return newDate.toISOString().split("T")[0];
  };

  const handleFileSelect = (files: any) => {
    setUploadedFile(files);
  };

  useEffect(() => {
    if (clearFileUploader) {
      setClearFileUploader(false);
    }
  }, [clearFileUploader]);

  const [bulkUploadDate, setBulkUploadDate] = useState("");
  const [isBulkUploadDateInvalid, setIsBulkUploadDateInvalid] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<any>({
    file: "",
  });

  const handleErrors = (error: any) => {
    setFieldErrors((previouseErrors: any) => {
      return { ...previouseErrors, ...error };
    });
  };

  const handleBulkUpload = () => {
    let isvalid = true;

    if (sourceOfInquiry === "" || sourceOfInquiry === "Select an option") {
      setIsSourceOfInquiryInvalid(true);
      isvalid = false;
    } else {
      setIsSourceOfInquiryInvalid(false);
    }

    if (sourceOfInquiry === "2" && representativeName === "") {
      setIsRepresentativeNameInvalid(true);
      console.log("Representative Name is invalid");
      isvalid = false;
    } else {
      setIsRepresentativeNameInvalid(false);
      console.log(representativeName, "Representative Name is valid");
    }

    if (
      [5, 7, 8, 9, 11].includes(Number(sourceOfInquiry)) &&
      selectedCampaign === ""
    ) {
      setIsCampaignInValid(true);
      isvalid = false;
    } else {
      setIsCampaignInValid(false);
    }

    if (sourceOfInquiry === "4" && socialMediaType === "") {
      setIsSocialMediaTypeInvalid(true);
      isvalid = false;
    } else {
      setIsSocialMediaTypeInvalid(false);
    }

    if (preferredDegree === "" || preferredDegree === "Select an option") {
      setIsPreferredDegreeInvalid(true);
      isvalid = false;
    } else {
      setIsPreferredDegreeInvalid(false);
    }

    if (preferredDegree !== "" && selectedCourses.length === 0) {
      setIsPreferredCourseInvalid(true);
      toast.error("Please select a preferred course");
      isvalid = false;
    } else {
      setIsPreferredCourseInvalid(false);
    }

    if (uploadedFile.length === 0) {
      handleErrors({ file: "File is required" });
      isvalid = false;
    }

    // if (fieldErrors.file === "" && isvalid) {

    if (isvalid) {
      // Collect all form data
      const formData = new FormData();

      if (uploadedFile && uploadedFile.length > 0) {
        uploadedFile.forEach((file) => {
          formData.append("file", file);
        });
      }

      const payload = {
        sourceOfInquiry: {
          id: sourceOfInquiry,
        },
        social_media_type: socialMediaType,
        courseCategoryId: preferredDegree,
        courseId: selectedCourses,
        campaign: {
          id: selectedCampaign,
        },
      };

      const filters = {
        sourceOfInquiry: {
          id: sourceOfInquiry,
        },
        socialMediaType: socialMediaType,
        courseCategoryId: preferredDegree,
        courseId: selectedCourses,
        campaign: {
          id: selectedCampaign,
        },
      };

      formData.append("filters", JSON.stringify(filters));
      formData.append("payloadDefaults", JSON.stringify(payload));

      formData.forEach((value, key) => {
        console.log(key, value);
      });

      Swal.fire({
        title: "Do You Want To Bulk Upload The Leads?",
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: "Upload",
        denyButtonText: `Don't Upload`,
      }).then((result) => {
        if (result.isConfirmed) {
          const token = sessionStorage.getItem("token");
          if (!token) {
            console.error("No token found. Please log in again.");
            return;
          }

          try {
            const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
            const currentTime = Math.floor(Date.now() / 1000);

            console.log("currentTime", currentTime);
            console.log("Token", token);

            if (decodedToken.exp && decodedToken.exp > currentTime) {
              const toastId = toast.loading("Uploading Data...");
              // Token is valid, proceed with the API call
              apiDefinitions
                .leadBulkUpload(token, formData, employeeId)
                .then((response: any) => {
                  if (response.data.code === 200) {
                    toast.dismiss(toastId);
                    toast.success("Bulk Upload Completed Successfully", {
                      duration: 5000,
                    });
                    window.location.reload();
                  } else {
                    toast.dismiss(toastId);
                    toast.error("Failed to Upload The Bulk");
                  }
                });
            } else {
              console.log("Session expired. Please log in again.");
              navigate(route.login);
            }
          } catch (error) {
            console.error("Invalid token or unable to decode", error);
          }
        } else {
          toast("Bulk Upload Cancelled", {
            icon: "⚠️",
          });
        }
      });
    } else {
      console.error(
        "Form validation failed. Please check the form for errors."
      );
    }
  };

  const handleLeadExport = () => {
    let isvalid = true;

    // if (
    //   sourceOfInquiryExport === "" ||
    //   sourceOfInquiryExport === "Select an option"
    // ) {
    //   setIsSourceOfInquiryInvalidExport(true);
    //   isvalid = false;
    // } else {
    //   setIsSourceOfInquiryInvalidExport(false);
    // }

    // if (
    //   sourceOfInquiryExport === "" ||
    //   sourceOfInquiryExport === "Select an option" ||
    //   preferredDegreeExport === "" ||
    //   preferredDegreeExport === "Select an option"
    // ) {
    //   isvalid = false;
    //   toast.error("Please Select At Least One Criteria")
    // }

    // if (assignedBy === "") {
    //   isvalid = false;
    //   setIsAssignedByValid(true);
    // } else {
    //   setIsAssignedByValid(false);
    // }

    if (sourceOfInquiryExport === "2" && representativeNameExport === "") {
      setIsRepresentativeNameInvalidExport(true);
      console.log("Representative Name is invalid");
      isvalid = false;
    } else {
      setIsRepresentativeNameInvalidExport(false);
      console.log(representativeName, "Representative Name is valid");
    }

    if (
      [5, 7, 8, 9, 11].includes(Number(sourceOfInquiryExport)) &&
      selectedCampaignExport === ""
    ) {
      setIsCampaignInValidExport(true);
      isvalid = false;
    } else {
      setIsCampaignInValidExport(false);
    }

    if (sourceOfInquiryExport === "4" && socialMediaTypeExport === "") {
      setIsSocialMediaTypeInvalidExport(true);
      isvalid = false;
    } else {
      setIsSocialMediaTypeInvalidExport(false);
    }

    // if (
    //   preferredDegreeExport === "" ||
    //   preferredDegreeExport === "Select an option"
    // ) {
    //   setIsPreferredDegreeInvalidExport(true);
    //   isvalid = false;
    // } else {
    //   setIsPreferredDegreeInvalidExport(false);
    // }

    // if (startDate !== "") {
    //   setIsStartDateInvalid(false);
    // }

    if (preferredDegreeExport !== "" && selectedCoursesExport.length === 0) {
      setIsPreferredCourseInvalidExport(true);
      toast.error("Please select a preferred course");
      isvalid = false;
    } else {
      setIsPreferredCourseInvalidExport(false);
    }

    if (isvalid) {
      const formData = new FormData();

      const payload = {
        sourceOfInquiry: {
          id: sourceOfInquiryExport,
        },
        socialMediaType: socialMediaTypeExport,
        courseCategoryId: preferredDegreeExport,
        courseId: selectedCoursesExport,
        campaign: {
          id: selectedCampaignExport,
        },
        leadStatus: {
          id: leadStatusExport,
        },
        assignedBy: assignedBy,
        startDate: startDate,
        endDate: endDate,
      };

      formData.append("filters", JSON.stringify(payload));

      Swal.fire({
        title: "Do You Want To Export The Leads?",
        showDenyButton: true,
        showCancelButton: false,
        confirmButtonText: "Export",
        denyButtonText: `Don't Export`,
      }).then(async (result) => {
        if (result.isConfirmed) {
          const token = sessionStorage.getItem("token");
          if (!token) {
            console.error("No token found. Please log in again.");
            return;
          }

          try {
            const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
            const currentTime = Math.floor(Date.now() / 1000);

            if (decodedToken.exp && decodedToken.exp > currentTime) {
              const toastId = toast.loading("Exporting Data...");
              // Token is valid, proceed with the API call
              const response = await apiDefinitions.leadExport(token, formData);

              if (response.data.code === 200) {
                toast.dismiss(toastId);
                console.log("Response", response.data.data);

                // Generate Excel file
                const leads = response.data.data;
                const worksheetData = leads.map((lead: any) => ({
                  NIC: lead.nic || "N/A", // Fallback for null/undefined values
                  Fullname: lead.fullname || "N/A",
                  Email: lead.email || "N/A",
                  "Contact Number": lead.contactNumber || "N/A",
                  FollowUp: lead.followUp || "N/A",
                  "Source of Inquiry": lead.sourceOfInquiry?.source || "N/A", // Handle nested objects
                  "Lead Status": lead.leadStatus?.status_name || "N/A", // Handle nested objects
                  Note: lead.note || "N/A",
                  "Follow Up Date": lead.followUpDate || "N/A",
                  "Created Date": lead.createdDate || "N/A",
                }));

                console.log("Worksheet Data:", worksheetData);

                const worksheet = XLSX.utils.json_to_sheet(worksheetData);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

                // Set custom column widths
                const columnWidths = [
                  { wch: 15 }, // NIC
                  { wch: 20 }, // Fullname
                  { wch: 25 }, // Email
                  { wch: 15 }, // Contact Number
                  { wch: 15 }, // FollowUp
                  { wch: 20 }, // Source of Inquiry
                  { wch: 15 }, // Lead Status
                  { wch: 30 }, // Note
                  { wch: 15 }, // Follow Up Date
                  { wch: 15 }, // Created Date
                ];

                worksheet["!cols"] = columnWidths;

                // Generate Excel file and trigger download
                XLSX.writeFile(workbook, "LeadsExport.xlsx");

                toast.success("Export Completed Successfully", {
                  duration: 5000,
                });
              } else if (response.data.code === 404) {
                toast.dismiss(toastId);
                toast.error("No Leads Were Found");
              } else {
                toast.dismiss(toastId);
                toast.error("Failed to Export The Leads");
              }
            } else {
              console.log("Session expired. Please log in again.");
              navigate(route.login);
            }
          } catch (error) {
            console.error("Invalid token or unable to decode", error);
          }
        } else {
          toast("Export Cancelled", {
            icon: "⚠️",
          });
        }
      });
    } else {
      console.error(
        "Form validation failed. Please check the form for errors."
      );
    }
  };

  const handleClear = () => {
    setUploadedFile([]);
    setClearFileUploader(true);

    // Clear errors
    setFieldErrors({ file: "" });
  };
  const [sourceOfInquiry, setSourceOfInquiry] = useState("");
  const [sourceOfInquiryExport, setSourceOfInquiryExport] = useState("");
  const [representativeName, setRepresentativeName] = useState("");
  const [representativeNameExport, setRepresentativeNameExport] = useState("");
  const [socialMediaType, setSocialMediaType] = useState("");
  const [socialMediaTypeExport, setSocialMediaTypeExport] = useState("");
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [selectedCampaignExport, setSelectedCampaignExport] = useState("");

  const [isSourceOfInquiryInvalid, setIsSourceOfInquiryInvalid] =
    useState(false);
  const [isSourceOfInquiryInvalidExport, setIsSourceOfInquiryInvalidExport] =
    useState(false);
  const [isRepresentativeNameInvalid, setIsRepresentativeNameInvalid] =
    useState(false);
  const [
    isRepresentativeNameInvalidExport,
    setIsRepresentativeNameInvalidExport,
  ] = useState(false);
  const [isSocialMediaTypeInvalid, setIsSocialMediaTypeInvalid] =
    useState(false);
  const [isSocialMediaTypeInvalidExport, setIsSocialMediaTypeInvalidExport] =
    useState(false);
  const [isCampaignInvalid, setIsCampaignInValid] = useState(false);
  const [isCampaignInvalidExport, setIsCampaignInValidExport] = useState(false);
  const [isPreferredDegreeInvalid, setIsPreferredDegreeInvalid] =
    useState(false);
  const [isPreferredDegreeInvalidExport, setIsPreferredDegreeInvalidExport] =
    useState(false);
  const [isPreferredCourseInvalid, setIsPreferredCourseInvalid] =
    useState(false);
  const [isPreferredCourseInvalidExport, setIsPreferredCourseInvalidExport] =
    useState(false);

  const [campaignList, setCampaignList] = useState<Category[]>([]);
  const [alumniList, setAlumniList] = useState<Alumni[]>([]);
  const [exhibitionList, setExhibitionList] = useState<Exhibition[]>([]);
  const [broadcastList, setBroadcastList] = useState<Broadcast[]>([]);
  const [googleType1List, setGoogleType1List] = useState<Google[]>([]);
  const [googleType2List, setGoogleType2List] = useState<Google[]>([]);
  const [googleType3List, setGoogleType3List] = useState<Google[]>([]);
  const [preferredDegree, setPreferredDegree] = useState("");
  const [preferredDegreeExport, setPreferredDegreeExport] = useState("");
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
  const [coursesList, setCoursesList] = useState<Courses[]>([]);
  const [selectedCoursesExport, setSelectedCoursesExport] = useState<number[]>(
    []
  );
  const [coursesFilterList, setCoursesFilterList] = useState<any[]>([]);
  const [coursesCategoryList, setCoursesCategoryList] = useState<any[]>([]);
  const [assignedBy, setAssignedBy] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [isEndDateInvalid, setIsEndDateInvalid] = useState(false);
  const [isStartDateInvalid, setIsStartDateInvalid] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  interface Category {
    id: number;
    name: string;
  }

  interface Alumni {
    id: number;
    name: string;
  }

  interface Exhibition {
    id: number;
    name: string;
  }

  interface Broadcast {
    id: number;
    name: string;
  }

  interface Google {
    id: number;
    name: string;
  }

  interface Courses {
    course_id: number;
    course_name: string;
  }

  interface Option {
    value: number;
    label: string;
  }

  const options: Option[] = coursesList.map((course) => ({
    value: course.course_id,
    label: course.course_name,
  }));

  const renderCoursesPopover = (courses: any[]) => (
    <Popover id="courses-popover">
      <Popover.Body>
        <ul style={{ listStyleType: "disc", paddingLeft: "20px" }}>
          {courses.map((course, index) => (
            <li key={index}>{course.course_name}</li>
          ))}
        </ul>
      </Popover.Body>
    </Popover>
  );

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getAllActiveCampaigns(token).then((response: any) => {
          console.log("Campaign Categories: ", response.data.data);
          setCampaignList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getCourses(token).then((response: any) => {
          setCoursesFilterList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getAlumniCampaigns(token).then((response: any) => {
          setAlumniList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getAllSourceOfInquiries(token).then((response: any) => {
          setInquiryList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getExhibitionCampaigns(token).then((response: any) => {
          setExhibitionList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getBroadcastCampaigns(token).then((response: any) => {
          setBroadcastList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getGoogleType1Campaigns(token).then((response: any) => {
          setGoogleType1List(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getGoogleType2Campaigns(token).then((response: any) => {
          setGoogleType2List(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getGoogleType3Campaigns(token).then((response: any) => {
          setGoogleType3List(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getAllCoursesCategories(token).then((response: any) => {
          console.log("Course Categories: ", response.data.data);
          setCoursesCategoryList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);
      console.log("Decoded Token", decodedToken);

      const degreeToUse = preferredDegreeExport || preferredDegree;

      if (degreeToUse) {
        if (decodedToken.exp && decodedToken.exp > currentTime) {
          apiDefinitions
            .getCoursesByCategoryId(token, degreeToUse)
            .then((response: any) => {
              console.log("Response", response.data.data);
              setCoursesList(response.data.data);
            });
        } else {
          console.log("Session expired. Please log in again.");
          navigate(route.login);
        }
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, [preferredDegree, preferredDegreeExport]);

  const handleBulkClear = () => {
    setSourceOfInquiry("");
    setRepresentativeName("");
    setSocialMediaType("");
    setSelectedCampaign("");
    setBulkUploadDate("");
    setPreferredDegree("");
    setSelectedCourses([]);
    setIsBulkUploadDateInvalid(false);
    setIsSourceOfInquiryInvalid(false);
    setIsRepresentativeNameInvalid(false);
    setIsSocialMediaTypeInvalid(false);
    setIsCampaignInValid(false);
    setIsPreferredDegreeInvalid(false);
    setIsPreferredCourseInvalid(false);
    setUploadedFile([]);
    handleErrors({ file: "" });
    setClearFileUploader(true);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      console.log("Decoded Token", decodedToken);

      setEmployeeId(decodedToken.serial_number);
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  const handleExportClean = () => {
    setSourceOfInquiryExport("");
    setRepresentativeNameExport("");
    setSocialMediaTypeExport("");
    setSelectedCampaignExport("");
    setPreferredDegreeExport("");
    setSelectedCoursesExport([]);
    setStartDate("");
    setEndDate("");
    setLeadStatusExport("");
    setAssignedBy("");
    setIsSourceOfInquiryInvalidExport(false);
    setIsRepresentativeNameInvalidExport(false);
    setIsSocialMediaTypeInvalidExport(false);
    setIsCampaignInValidExport(false);
    setIsPreferredDegreeInvalidExport(false);
    setIsPreferredCourseInvalidExport(false);
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getMarketingEmployeeList(token).then((response: any) => {
          console.log("Course Categories: ", response.data.data);
          setEmployeeList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("token");

    if (!token) {
      console.error("No token found. Please log in again.");
      return;
    }

    try {
      const decodedToken: DecodedToken = jwtDecode(token) as DecodedToken;
      const currentTime = Math.floor(Date.now() / 1000);

      if (decodedToken.exp && decodedToken.exp > currentTime) {
        apiDefinitions.getAllRepresentativeList(token).then((response: any) => {
          setRepresentativeList(response.data.data);
        });
      } else {
        console.log("Session expired. Please log in again.");
        navigate(route.login);
      }
    } catch (error) {
      console.error("Invalid token or unable to decode", error);
    }
  }, []);

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="row">
          <div className="col-md-12">
            <div className="col-xl-12">
              <div className="card">
                <div className="card-header">
                  <div className="card-title">View All Leads</div>
                  <div className="row">
                    {/* Pagination Dropdown */}
                    <div className="col-md-1 mb-3 mt-3">
                      <Form.Select
                        value={itemsPerPage}
                        onChange={(event) =>
                          setItemsPerPage(parseInt(event.target.value, 10))
                        }
                        aria-label="Select number of items per page"
                        className="w-auto"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </Form.Select>
                    </div>

                    {/* Search Input */}
                    <div className="col-md-4 mt-3">
                      <input
                        type="text"
                        className="form-control"
                        id="validationCustom01"
                        placeholder="Search"
                        value={searchKey}
                        onChange={(e) => setSearchKey(e.target.value)}
                        required
                      />
                    </div>

                    {/* Search Button */}
                    <div
                      className="col-md-1 mt-3"
                      style={{ textAlign: "start" }}
                    >
                      <button
                        className="btn btn-primary"
                        onClick={searchClicked ? handleClose : handleSearch}
                      >
                        <i
                          className={`fas ${
                            searchClicked ? "fa-times" : "fa-search"
                          }`}
                          style={{
                            marginRight: "5px",
                            padding: "4px",
                            fontSize: "15px",
                            marginLeft: "1px",
                          }}
                        ></i>
                      </button>
                    </div>

                    <div className="col-md-6 mt-3" style={{ textAlign: "end" }}>
                      <button
                        className="btn btn-outline-secondary"
                        onClick={toggleFilters}
                      >
                        <i
                          className={`fas ${
                            showFilters ? "fa-chevron-up" : "fa-chevron-down"
                          }`}
                          style={{
                            padding: "4px",
                            fontSize: "15px",
                          }}
                        ></i>{" "}
                        {showFilters ? "Hide Filters" : "Show Filters"}
                      </button>
                    </div>

                    {showFilters && (
                      <>
                        <div className="col-md-3 mb-3 mt-3 position-relative">
                          <select
                            className="form-select"
                            id="validationCustomContactMethod"
                            value={leadStatus}
                            onChange={(e) => setLeadStatus(e.target.value)}
                            required
                          >
                            <option>Select a Lead Status</option>
                            <option value="1">New</option>
                            <option value="2">Contacted</option>
                            <option value="3">Interested</option>
                            <option value="4">Application in Progress</option>
                            <option value="5">Enrolled</option>
                            <option value="6">Disqualified</option>
                            <option value="8">Lost</option>
                            <option value="9">Not Responded</option>
                            <option value="10">Next Intake</option>
                          </select>
                          {leadStatus && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light position-absolute"
                              style={{
                                right: "15px",
                                top: "50%",
                                transform: "translateY(-50%)",
                              }}
                              onClick={() => setLeadStatus("")}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>

                        <div className="col-md-3 mt-3 position-relative">
                          <select
                            className="form-select"
                            id="validationCustomContactMethod"
                            value={leadPriority}
                            onChange={(e) => setLeadPriority(e.target.value)}
                            required
                          >
                            <option>Select a Priority Level</option>
                            <option value="1">Low</option>
                            <option value="2">Medium</option>
                            <option value="3">High</option>
                          </select>
                          {leadPriority && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light position-absolute"
                              style={{
                                right: "15px",
                                top: "35%",
                                transform: "translateY(-50%)",
                              }}
                              onClick={() => setLeadPriority("")}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>

                        <div className="col-md-3 mt-3 position-relative">
                          <select
                            className="form-select"
                            id="validationCustomContactMethod"
                            value={filterCourseId}
                            onChange={(e) => setFilterCourseId(e.target.value)}
                          >
                            <option value="">Select a Course</option>
                            {coursesFilterList.map((course) => (
                              <option
                                key={course.course_id}
                                value={course.course_id}
                              >
                                {course.course_name}
                              </option>
                            ))}
                          </select>
                          {filterCourseId && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light position-absolute"
                              style={{
                                right: "15px",
                                top: "35%",
                                transform: "translateY(-50%)",
                              }}
                              onClick={() => setFilterCourseId("")}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>

                        <div className="col-md-3 mt-3 position-relative">
                          <select
                            className="form-select"
                            id="validationCustomContactMethod"
                            value={filterAssignedBy}
                            onChange={(e) =>
                              setFilterAssignedBy(e.target.value)
                            }
                          >
                            <option value="">Select an Assigned By</option>
                            {employeeList.map((employeee) => (
                              <option
                                key={employeee.serial_number}
                                value={employeee.serial_number}
                              >
                                {employeee.name_in_full}
                              </option>
                            ))}
                          </select>
                          {filterAssignedBy && (
                            <button
                              type="button"
                              className="btn btn-sm btn-light position-absolute"
                              style={{
                                right: "15px",
                                top: "35%",
                                transform: "translateY(-50%)",
                              }}
                              onClick={() => setFilterAssignedBy("")}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          )}
                        </div>

                        <div
                          className="col-md-12 mt-3"
                          style={{ textAlign: "start" }}
                        >
                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light mt-1 me-1"
                            data-bs-toggle="modal"
                            data-bs-target="#con-close-modal"
                          >
                            <i
                              className="fas fa-upload"
                              style={{
                                marginRight: "5px",
                                padding: "4px",
                                fontSize: "15px",
                                marginLeft: "1px",
                              }}
                            ></i>
                            Bulk Upload
                          </button>

                          <button
                            type="button"
                            className="btn btn-primary waves-effect waves-light mt-1 me-1"
                            data-bs-toggle="modal"
                            data-bs-target="#lead-export-modal"
                          >
                            <i
                              className="fas fa-file-export"
                              style={{
                                marginRight: "5px",
                                padding: "4px",
                                fontSize: "15px",
                                marginLeft: "1px",
                              }}
                            ></i>
                            Lead Export
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table text-nowrap table-striped">
                      <thead>
                        <tr>
                          <th scope="col">Full Name</th>
                          <th scope="col">NIC</th>
                          <th scope="col">Contact Number</th>
                          <th scope="col">Follow Up Date</th>
                          <th scope="col">Follow Up Status</th>
                          <th scope="col">Assigned By</th>{" "}
                          {/* Updated column header */}
                          <th scope="col">Courses</th>
                          <th scope="col">Lead Priority</th>
                          <th scope="col">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {leads.length > 0 ? (
                          leads.map((lead, index) => (
                            <tr key={index}>
                              <td>
                                {lead?.fullname?.length > 30 ? (
                                  <OverlayTrigger
                                    trigger={["hover", "focus"]}
                                    placement="top"
                                    overlay={renderReasonPopover({
                                      reason: lead.fullname.toUpperCase(),
                                    })}
                                  >
                                    <span style={{ cursor: "pointer" }}>
                                      {lead.fullname
                                        .substring(0, 30)
                                        .toUpperCase()}
                                      ...
                                    </span>
                                  </OverlayTrigger>
                                ) : (
                                  lead.fullname.toUpperCase()
                                )}
                              </td>
                              <td>{lead.nic}</td>
                              <td>{lead.contactNumber}</td>
                              <td>
                                {lead.followUpDate
                                  ? new Date(lead.followUpDate)
                                      .toISOString()
                                      .split("T")[0]
                                  : "N/A"}
                              </td>
                              <td>
                                <span
                                  className={`badge bg-soft-${
                                    lead.followUp === "Follow-Up-Started"
                                      ? "success"
                                      : lead.followUp === "Follow-Up-Needed"
                                      ? "info"
                                      : "danger"
                                  }`}
                                >
                                  {lead.followUp}
                                </span>
                              </td>
                              <td>
                                {(() => {
                                  const employee = employeeList.find(
                                    (employee) =>
                                      employee.serial_number ===
                                      Number(lead.assignedBy)
                                  );
                                  if (!employee) return "N/A";
                                  return employee.name_in_full.length > 30 ? (
                                    <OverlayTrigger
                                      trigger={["hover", "focus"]}
                                      placement="top"
                                      overlay={renderReasonPopover({
                                        reason:
                                          employee.name_in_full.toUpperCase(),
                                      })}
                                    >
                                      <span style={{ cursor: "pointer" }}>
                                        {employee.name_in_full
                                          .substring(0, 30)
                                          .toUpperCase()}
                                        ...
                                      </span>
                                    </OverlayTrigger>
                                  ) : (
                                    employee.name_in_full.toUpperCase()
                                  );
                                })()}
                              </td>
                              <td>
                                {lead.courseId?.length > 0 ? (
                                  <OverlayTrigger
                                    trigger={["hover", "focus"]}
                                    placement="bottom"
                                    overlay={renderCoursesPopover(
                                      lead.courseId
                                    )}
                                  >
                                    <button
                                      className="btn btn-outline-success btn-wave"
                                      type="button"
                                    >
                                      View Courses
                                    </button>
                                  </OverlayTrigger>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              {/* <td>
                                <span
                                  className={`badge bg-soft-${lead.leadStatus?.color}`}
                                >
                                  {lead.leadStatus?.status_name}
                                </span>
                              </td> */}
                              <td>
                                <span
                                  className={`badge bg-soft-${
                                    lead.leadPriorityLevelId === 1
                                      ? "secondary"
                                      : lead.leadPriorityLevelId === 2
                                      ? "success"
                                      : lead.leadPriorityLevelId === 3
                                      ? "danger"
                                      : "secondary"
                                  }`}
                                >
                                  {lead.leadPriorityLevelId === 1
                                    ? "Low"
                                    : lead.leadPriorityLevelId === 2
                                    ? "Medium"
                                    : lead.leadPriorityLevelId === 3
                                    ? "High"
                                    : "N/A"}
                                </span>
                              </td>
                              <td>
                                <div className="hstack gap-2 fs-15">
                                  <Link
                                    to={`${route.updateLeads
                                      .replace(":id", lead.id.toString())
                                      .replace(":status", "false")}`}
                                    className="btn btn-icon btn-md btn-info"
                                  >
                                    <i
                                      className="feather-edit"
                                      style={{ fontSize: "17px" }}
                                    />
                                  </Link>
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={9} className="text-center">
                              No Data Available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <Pagination className="mt-3 justify-content-center">
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />

                    {/* Calculate the range of pages to display */}
                    {(() => {
                      const maxPagesToShow = 10; // Show only 10 page numbers at a time
                      const halfRange = Math.floor(maxPagesToShow / 2);
                      let startPage = Math.max(1, currentPage - halfRange);
                      let endPage = Math.min(
                        totalPages,
                        startPage + maxPagesToShow - 1
                      );

                      // Adjust startPage if endPage reaches totalPages
                      if (endPage - startPage + 1 < maxPagesToShow) {
                        startPage = Math.max(1, endPage - maxPagesToShow + 1);
                      }

                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <Pagination.Item
                            key={i}
                            active={i === currentPage}
                            onClick={() => handlePageChange(i)}
                          >
                            {i}
                          </Pagination.Item>
                        );
                      }
                      return pages;
                    })()}

                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className="modal fade"
        id="con-close-modal"
        tabIndex={-1}
        aria-labelledby="con-close-modal-label"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="con-close-modal-label">
                Lead Bulk Upload
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleBulkClear}
              ></button>
            </div>

            <div className="modal-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <label
                    className="form-label"
                    htmlFor="validationCustomContactMethod"
                  >
                    Source of Inquiry
                  </label>
                  <select
                    className={`form-select ${
                      isSourceOfInquiryInvalid ? "is-invalid" : ""
                    }`}
                    id="validationCustomContactMethod"
                    value={sourceOfInquiry}
                    onChange={(e) => setSourceOfInquiry(e.target.value)}
                    required
                  >
                    <option value="">Select an option</option>
                    {inquiryList.map((inquiry) => (
                      <option key={inquiry.id} value={inquiry.id}>
                        {inquiry.source}
                      </option>
                    ))}
                  </select>
                  {isSourceOfInquiryInvalid && (
                    <div className="invalid-feedback">
                      Please select a source of inquiry.
                    </div>
                  )}
                </div>

                {sourceOfInquiry === "11" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Broadcast Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaign}
                      onChange={(e) => {
                        setSelectedCampaign(e.target.value);
                        console.log("Selected Campaign", e.target.value);
                      }}
                      required
                    >
                      <option value="">Select an option</option>
                      {broadcastList.map((broadcast: Broadcast) => (
                        <option key={broadcast.id} value={broadcast.id}>
                          {broadcast.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalid && (
                      <div className="invalid-feedback">
                        Please select an broadcast campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "9" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Google Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      {googleType1List.map((googleType1: Google) => (
                        <option key={googleType1?.id} value={googleType1?.id}>
                          {googleType1?.name}
                        </option>
                      ))}
                      {googleType2List.map((googleType2: Google) => (
                        <option key={googleType2?.id} value={googleType2?.id}>
                          {googleType2?.name}
                        </option>
                      ))}
                      {googleType3List.map((googleType3: Google) => (
                        <option key={googleType3?.id} value={googleType3?.id}>
                          {googleType3?.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalid && (
                      <div className="invalid-feedback">
                        Please select an google campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "8" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Alumni Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      {alumniList.map((alumni: Alumni) => (
                        <option key={alumni.id} value={alumni.id}>
                          {alumni.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalid && (
                      <div className="invalid-feedback">
                        Please select an alumni campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "7" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Exhibition Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      {exhibitionList.map((exhibition: Exhibition) => (
                        <option key={exhibition.id} value={exhibition.id}>
                          {exhibition.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalid && (
                      <div className="invalid-feedback">
                        Please select an exhibition campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "4" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Social Media Type
                    </label>
                    <select
                      className={`form-select ${
                        isSocialMediaTypeInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={socialMediaType}
                      onChange={(e) => setSocialMediaType(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      <option value="5">YouTube</option>
                      <option value="1">Facebook</option>
                      <option value="2">Instagram</option>
                      <option value="3">LinkedIn</option>
                      <option value="4">Tik Tok</option>
                    </select>
                    {isSocialMediaTypeInvalid && (
                      <div className="invalid-feedback">
                        Please select a social media type.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "5" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaign}
                      onChange={(e) => setSelectedCampaign(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      {campaignList.map((campaign: Category) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalid && (
                      <div className="invalid-feedback">
                        Please select a campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiry === "2" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Representative's Name
                    </label>
                    <select
                      className={`form-select ${
                        isRepresentativeNameInvalid ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={representativeName}
                      onChange={(e) => setRepresentativeName(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      {representativeList.map((alumni: Alumni) => (
                        <option key={alumni.id} value={alumni.id}>
                          {alumni.name}
                        </option>
                      ))}
                    </select>
                    {isRepresentativeNameInvalid && (
                      <div className="invalid-feedback">
                        Please select a representative's name.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="col-md-6 mb-3 mt-3">
                <label
                  className="form-label"
                  htmlFor="validationCustomContactMethod"
                >
                  Preferred Degree
                </label>
                <select
                  className={`form-select ${
                    isPreferredDegreeInvalid ? "is-invalid" : ""
                  }`}
                  id="validationCustomContactMethod"
                  value={preferredDegree}
                  onChange={(e) => {
                    setPreferredDegree(e.target.value);
                    setPreferredDegreeExport("");
                  }}
                  required
                >
                  <option value="">Select an option</option>
                  {coursesCategoryList.map((courseCategory) => (
                    <option
                      key={courseCategory.course_category_id}
                      value={courseCategory.course_category_id}
                    >
                      {courseCategory.category_name}
                    </option>
                  ))}
                </select>
                {isPreferredDegreeInvalid && (
                  <div className="invalid-feedback">
                    Please select a preferred degree.
                  </div>
                )}
              </div>

              <div className="col-md-12 mb-3">
                <label
                  className="form-label"
                  htmlFor="validationCustomContactMethod"
                >
                  Preferred Course
                </label>

                <Select
                  classNamePrefix="react-select"
                  className="js-example-placeholder-multiple select2 js-states"
                  isMulti={true}
                  placeholder="Select"
                  options={options}
                  value={options.filter((option) =>
                    selectedCourses.includes(option.value)
                  )}
                  onChange={(selectedOptions) => {
                    const courseValues = selectedOptions.map(
                      (option) => option.value
                    );
                    setSelectedCourses(courseValues);
                    // setIsPreferredCourseInvalid(
                    //   courseValues.length === 0
                    // );
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "40px",
                      height: "auto",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "5px 10px",
                      flexWrap: "wrap",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      margin: "2px",
                    }),
                  }}
                />

                {isPreferredCourseInvalid && (
                  <div className="invalid-feedback">
                    Please select a preferred course.
                  </div>
                )}
              </div>

              <div className="row">
                <div className="modal-body">
                  <FileUpload
                    onFileSelect={handleFileSelect}
                    clear={clearFileUploader}
                  />
                  {fieldErrors.file !== "" ? (
                    <div className="text-danger">{fieldErrors.file}</div>
                  ) : null}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-cancel waves-effect"
                data-bs-dismiss="modal"
                onClick={handleBulkClear}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light"
                onClick={handleBulkUpload}
              >
                Upload
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        className="modal fade"
        id="lead-export-modal"
        tabIndex={-1}
        aria-labelledby="lead-export-modal-label"
        aria-hidden="true"
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="lead-export-modal-label">
                Lead Export
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleExportClean}
              ></button>
            </div>
            <div className="modal-body p-4">
              <div className="row">
                <div className="col-md-6">
                  <label
                    className="form-label"
                    htmlFor="validationCustomContactMethod"
                  >
                    Source of Inquiry
                  </label>
                  <select
                    className={`form-select ${
                      isSourceOfInquiryInvalidExport ? "is-invalid" : ""
                    }`}
                    id="validationCustomContactMethod"
                    value={sourceOfInquiryExport}
                    onChange={(e) => setSourceOfInquiryExport(e.target.value)}
                    required
                  >
                    <option value="">Select an option</option>
                    {inquiryList.map((inquiry) => (
                      <option key={inquiry.id} value={inquiry.id}>
                        {inquiry.source}
                      </option>
                    ))}
                  </select>
                  {isSourceOfInquiryInvalidExport && (
                    <div className="invalid-feedback">
                      Please select a source of inquiry.
                    </div>
                  )}
                </div>

                {sourceOfInquiryExport === "11" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Broadcast Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaignExport}
                      onChange={(e) => {
                        setSelectedCampaignExport(e.target.value);
                        console.log("Selected Campaign", e.target.value);
                      }}
                      required
                    >
                      <option value="">Select an option</option>
                      {broadcastList.map((broadcast: Broadcast) => (
                        <option key={broadcast.id} value={broadcast.id}>
                          {broadcast.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalidExport && (
                      <div className="invalid-feedback">
                        Please select an broadcast campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "9" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Google Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaignExport}
                      onChange={(e) =>
                        setSelectedCampaignExport(e.target.value)
                      }
                      required
                    >
                      <option value="">Select an option</option>
                      {googleType1List.map((googleType1: Google) => (
                        <option key={googleType1?.id} value={googleType1?.id}>
                          {googleType1?.name}
                        </option>
                      ))}
                      {googleType2List.map((googleType2: Google) => (
                        <option key={googleType2?.id} value={googleType2?.id}>
                          {googleType2?.name}
                        </option>
                      ))}
                      {googleType3List.map((googleType3: Google) => (
                        <option key={googleType3?.id} value={googleType3?.id}>
                          {googleType3?.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalidExport && (
                      <div className="invalid-feedback">
                        Please select an google campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "8" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Alumni Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaignExport}
                      onChange={(e) =>
                        setSelectedCampaignExport(e.target.value)
                      }
                      required
                    >
                      <option value="">Select an option</option>
                      {alumniList.map((alumni: Alumni) => (
                        <option key={alumni.id} value={alumni.id}>
                          {alumni.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalidExport && (
                      <div className="invalid-feedback">
                        Please select an alumni campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "7" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Exhibition Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaignExport}
                      onChange={(e) =>
                        setSelectedCampaignExport(e.target.value)
                      }
                      required
                    >
                      <option value="">Select an option</option>
                      {exhibitionList.map((exhibition: Exhibition) => (
                        <option key={exhibition.id} value={exhibition.id}>
                          {exhibition.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalidExport && (
                      <div className="invalid-feedback">
                        Please select an exhibition campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "4" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Social Media Type
                    </label>
                    <select
                      className={`form-select ${
                        isSocialMediaTypeInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={socialMediaTypeExport}
                      onChange={(e) => setSocialMediaTypeExport(e.target.value)}
                      required
                    >
                      <option value="">Select an option</option>
                      <option value="5">YouTube</option>
                      <option value="1">Facebook</option>
                      <option value="2">Instagram</option>
                      <option value="3">LinkedIn</option>
                      <option value="4">Tik Tok</option>
                    </select>
                    {isSocialMediaTypeInvalidExport && (
                      <div className="invalid-feedback">
                        Please select a social media type.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "5" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Campaign Name
                    </label>
                    <select
                      className={`form-select ${
                        isCampaignInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={selectedCampaignExport}
                      onChange={(e) =>
                        setSelectedCampaignExport(e.target.value)
                      }
                      required
                    >
                      <option value="">Select an option</option>
                      {campaignList.map((campaign: Category) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.name}
                        </option>
                      ))}
                    </select>
                    {isCampaignInvalidExport && (
                      <div className="invalid-feedback">
                        Please select a campaign name.
                      </div>
                    )}
                  </div>
                )}

                {sourceOfInquiryExport === "2" && (
                  <div className="col-md-6">
                    <label
                      className="form-label"
                      htmlFor="validationCustomContactMethod"
                    >
                      Representative's Name
                    </label>
                    <select
                      className={`form-select ${
                        isRepresentativeNameInvalidExport ? "is-invalid" : ""
                      }`}
                      id="validationCustomContactMethod"
                      value={representativeNameExport}
                      onChange={(e) =>
                        setRepresentativeNameExport(e.target.value)
                      }
                      required
                    >
                      <option value="">Select an option</option>
                      {representativeList.map((alumni: Alumni) => (
                        <option key={alumni.id} value={alumni.id}>
                          {alumni.name}
                        </option>
                      ))}
                    </select>
                    {isRepresentativeNameInvalid && (
                      <div className="invalid-feedback">
                        Please select a representative's name.
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="row">
                <div className="col-md-6 mb-3 mt-3">
                  <label
                    className="form-label"
                    htmlFor="validationCustomContactMethod"
                  >
                    Lead Status
                  </label>
                  <select
                    className={`form-select`}
                    id="validationCustomContactMethod"
                    value={leadStatusExport}
                    onChange={(e) => {
                      setLeadStatusExport(e.target.value);
                    }}
                    required
                  >
                    <option value="" disabled={true}>
                      Select an option
                    </option>
                    <option value="1">New</option>
                    <option value="2">Contacted</option>
                    <option value="3">Interested</option>
                    <option value="4">Application in Progress</option>
                    <option value="5">Enrolled</option>
                    <option value="6">Disqualified</option>
                    {/* <option value="7">Follow-Up Needed</option> */}
                    <option value="8">Lost</option>
                    <option value="9">Not Responded</option>
                    <option value="10">Next Intake</option>
                  </select>
                </div>

                <div className="col-md-6 mb-3 mt-3">
                  <label
                    className="form-label"
                    htmlFor="validationCustomContactMethod"
                  >
                    Preferred Degree
                  </label>
                  <select
                    className={`form-select ${
                      isPreferredDegreeInvalidExport ? "is-invalid" : ""
                    }`}
                    id="validationCustomContactMethod"
                    value={preferredDegreeExport}
                    onChange={(e) => {
                      setPreferredDegreeExport(e.target.value);
                      setPreferredDegree("");
                    }}
                    required
                  >
                    <option value="">Select an option</option>
                    {coursesCategoryList.map((courseCategory) => (
                      <option
                        key={courseCategory.course_category_id}
                        value={courseCategory.course_category_id}
                      >
                        {courseCategory.category_name}
                      </option>
                    ))}
                  </select>
                  {isPreferredDegreeInvalidExport && (
                    <div className="invalid-feedback">
                      Please select a preferred degree.
                    </div>
                  )}
                </div>
              </div>

              <div className="col-md-12 mb-3">
                <label
                  className="form-label"
                  htmlFor="validationCustomContactMethod"
                >
                  Preferred Course
                </label>

                <Select
                  classNamePrefix="react-select"
                  className="js-example-placeholder-multiple select2 js-states"
                  isMulti={true}
                  placeholder="Select"
                  options={options}
                  value={options.filter((option) =>
                    selectedCoursesExport.includes(option.value)
                  )}
                  onChange={(selectedOptions) => {
                    const courseValues = selectedOptions.map(
                      (option) => option.value
                    );
                    setSelectedCoursesExport(courseValues);
                    // setIsPreferredCourseInvalid(
                    //   courseValues.length === 0
                    // );
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      minHeight: "40px",
                      height: "auto",
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: "5px 10px",
                      flexWrap: "wrap",
                    }),
                    multiValue: (base) => ({
                      ...base,
                      margin: "2px",
                    }),
                  }}
                />

                {isPreferredCourseInvalid && (
                  <div className="invalid-feedback">
                    Please select a preferred course.
                  </div>
                )}
              </div>

              <div className="col-md-12 mb-3 mt-3">
                <label
                  className="form-label"
                  htmlFor="validationCustomContactMethod"
                >
                  Assigned By
                </label>
                <select
                  className={`form-select  ${
                    isAssignedByValid ? "is-invalid" : ""
                  }`}
                  id="validationCustomContactMethod"
                  value={assignedBy}
                  onChange={(e) => setAssignedBy(e.target.value)}
                >
                  <option value="">Select an option</option>
                  {employeeList.map((employeee) => (
                    <option
                      key={employeee.serial_number}
                      value={employeee.serial_number}
                    >
                      {employeee.name_in_full}
                    </option>
                  ))}
                </select>
                {isAssignedByValid && (
                  <div className="invalid-feedback">
                    Please select an assigned by.
                  </div>
                )}
              </div>

              {/* <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label" htmlFor="start-date">
                    Start Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="start-date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    required
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label" htmlFor="end-date">
                    End Date
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    id="end-date"
                    value={endDate}
                    min={startDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    required
                    disabled={!startDate}
                  />
                </div>
              </div> */}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-cancel waves-effect"
                data-bs-dismiss="modal"
                onClick={handleExportClean}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary waves-effect waves-light"
                onClick={handleLeadExport}
              >
                Export
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewAllLeads;
